<template>
  <div class="py-6 space-y-6 h-full flex flex-col bg-gray-50 overflow-y-auto custom-scrollbar">
    <!-- === HEADER === -->
    <div class="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
      <div class="flex items-center gap-4">
        <div class="w-16 h-16 rounded-2xl bg-primary-50 flex items-center justify-center text-primary-600 text-2xl font-bold border border-primary-100">
          {{ (customerData?.name || 'U').charAt(0).toUpperCase() }}
        </div>
        <div>
          <h1 class="text-2xl font-bold text-gray-900">{{ customerData?.name || 'Utilisateur' }}</h1>
          <p class="text-sm text-gray-500">{{ customerData?.email || 'Pas d\'email' }}</p>
          <div class="mt-2 flex items-center gap-2">
            <span class="px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider" :class="statusClass">
              {{ customerData?.status || 'Active' }}
            </span>
            <span class="text-xs text-gray-400">Membre depuis le {{ Commons.time(customerData?.createdAt) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- === STATS === -->
    <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
      <div class="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm text-center">
        <p class="text-[10px] text-gray-500 uppercase font-black tracking-widest mb-1">Commandes</p>
        <p class="text-xl font-black text-gray-900">{{ stats.totalOrders || 0 }}</p>
      </div>
      <div class="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm text-center">
        <p class="text-[10px] text-gray-500 uppercase font-black tracking-widest mb-1">Total Dépensé</p>
        <p class="text-xl font-black text-primary-600">{{ Commons.formatNumber(stats.totalSpent || 0, { suffix: ' F' }) }}</p>
      </div>
      <div class="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm text-center">
        <p class="text-[10px] text-gray-500 uppercase font-black tracking-widest mb-1">Panier Moyen</p>
        <p class="text-xl font-black text-gray-900">{{ Commons.formatNumber(stats.avgOrderValue || 0, { suffix: ' F' }) }}</p>
      </div>
      <div class="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm text-center">
        <p class="text-[10px] text-gray-500 uppercase font-black tracking-widest mb-1">Solde Wallet</p>
        <p class="text-xl font-black text-success-600">{{ Commons.formatNumber(customerData?.balance || 0, { suffix: ' F' }) }}</p>
      </div>
    </div>

    <!-- === INFO DETAILS === -->
      <div class="flex justify-between items-center mb-4">
        <h3 class="text-lg font-bold text-gray-900 flex items-center gap-2">
          <i class="pi pi-info-circle text-blue-500"></i>
          Informations personnelles
        </h3>
        <button 
          @click="isEditing = !isEditing"
          class="text-xs font-bold px-3 py-1 rounded-lg border transition-colors"
          :class="isEditing ? 'bg-gray-100 text-gray-600 border-gray-200' : 'bg-primary-50 text-primary-600 border-primary-100 uppercase'"
        >
          {{ isEditing ? 'Annuler' : 'Modifier' }}
        </button>
      </div>
      
      <div v-if="!isEditing" class="grid grid-cols-1 md:grid-cols-2 gap-6 scale-in-center">
        <div class="space-y-4">
          <div>
            <p class="text-xs text-gray-400 font-medium uppercase tracking-wider mb-1">Téléphone</p>
            <p class="text-sm font-bold text-gray-700">{{ customerData?.phone || customerData?.phoneNumber || '-' }}</p>
          </div>
          <div>
            <p class="text-xs text-gray-400 font-medium uppercase tracking-wider mb-1">WhatsApp</p>
            <p class="text-sm font-bold text-gray-700">{{ customerData?.whatsapp || '-' }}</p>
          </div>
          <div>
            <p class="text-xs text-gray-400 font-medium uppercase tracking-wider mb-1">Date de naissance</p>
            <p class="text-sm font-bold text-gray-700">{{ customerData?.birthday ? Commons.time(customerData.birthday) : '-' }}</p>
          </div>
        </div>
        <div class="space-y-4">
          <div>
            <p class="text-xs text-gray-400 font-medium uppercase tracking-wider mb-1">Numéro d’inscription</p>
            <p class="text-sm font-bold text-blue-600">{{ customerData?.registrationNumber || '-' }}</p>
          </div>
          <div>
            <p class="text-xs text-gray-400 font-medium uppercase tracking-wider mb-1">Dernière commande</p>
            <p class="text-sm font-bold text-gray-700">{{ stats.lastOrderDate ? Commons.time(stats.lastOrderDate) : 'Aucune' }}</p>
          </div>
           <div>
            <p class="text-xs text-gray-400 font-medium uppercase tracking-wider mb-1">ID Client</p>
            <p class="text-[10px] font-mono text-gray-400 break-all">{{ customerData?.id || '-' }}</p>
          </div>
        </div>
      </div>

      <div v-else class="space-y-6 scale-in-center">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div class="space-y-1">
            <label class="text-[10px] font-black text-gray-400 uppercase tracking-widest">WhatsApp</label>
            <input 
              v-model="editForm.whatsapp"
              type="text"
              class="w-full p-3 rounded-xl border border-gray-200 focus:border-primary-500 focus:ring-0 text-sm font-bold transition-all bg-gray-50"
              placeholder="Ex: +229..."
            />
          </div>
          <div class="space-y-1">
            <label class="text-[10px] font-black text-gray-400 uppercase tracking-widest">Date de naissance</label>
            <input 
              v-model="editForm.birthday"
              type="date"
              class="w-full p-3 rounded-xl border border-gray-200 focus:border-primary-500 focus:ring-0 text-sm font-bold transition-all bg-gray-50"
            />
          </div>
          <div class="space-y-1">
            <label class="text-[10px] font-black text-gray-400 uppercase tracking-widest">Numéro d’inscription</label>
            <input 
              v-model="editForm.registrationNumber"
              type="text"
              class="w-full p-3 rounded-xl border border-gray-200 focus:border-primary-500 focus:ring-0 text-sm font-bold transition-all bg-gray-50"
              placeholder="Ex: REG-2024-..."
            />
          </div>
        </div>
        
        <div class="flex justify-end gap-3 pt-4 border-t">
          <button 
            @click="isEditing = false"
            class="px-4 py-2 rounded-xl text-sm font-bold text-gray-500 hover:bg-gray-100 transition-all"
          >
            Annuler
          </button>
          <button 
            @click="saveProfile"
            class="px-6 py-2 rounded-xl text-sm font-bold text-white bg-primary-600 hover:bg-primary-700 shadow-lg shadow-primary-100 transition-all flex items-center gap-2"
          >
            <i class="pi pi-check"></i>
            Enregistrer
          </button>
        </div>
      </div>

 
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import Commons from '@/shared/utils/commons'

const props = defineProps({
  customerData: {
    type: Object,
    default: () => ({}),
  },
})

const emit = defineEmits(['update-status', 'delete', 'update-profile'])

const isEditing = ref(false)
const editForm = ref({
  whatsapp: '',
  birthday: '',
  registrationNumber: ''
})

watch(() => props.customerData, (newVal) => {
  if (newVal) {
    editForm.value = {
      whatsapp: newVal.whatsapp || '',
      birthday: newVal.birthday ? newVal.birthday.split('T')[0] : '',
      registrationNumber: newVal.registrationNumber || ''
    }
  }
}, { immediate: true })

const stats = computed(() => props.customerData?.stats || {})

const statusClass = computed(() => {
  const s = (props.customerData?.status || 'active').toLowerCase()
  if (s === 'active' || s === 'actif') return 'bg-success-100 text-success-700'
  if (s === 'suspended' || s === 'suspendu') return 'bg-warning-100 text-warning-700'
  return 'bg-gray-100 text-gray-700'
})

const saveProfile = () => {
  emit('update-profile', { ...editForm.value })
  isEditing.value = false
}
</script>

<style scoped>
.scale-in-center {
	animation: scale-in-center 0.2s cubic-bezier(0.250, 0.460, 0.450, 0.940) both;
}
@keyframes scale-in-center {
  0% { transform: scale(0.98); opacity: 0; }
  100% { transform: scale(1); opacity: 1; }
}

.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #e5e7eb;
  border-radius: 10px;
}
</style>
